// VirtualizedTablesTemplate.js - WITH REDUX INTEGRATION
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { VariableSizeList } from 'react-window';
import { useTable, useSortBy } from 'react-table';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateTableColumnWidths,
  updateTableSorting,
  updateTableSearch
} from '../../redux/panelPositionsSlice';
import { CLEAR_TABLE_DATA } from '../../common/common';
import './VirtualizedTablesTemplate.css';

const RowComponent = React.memo(({
  rowInfo,
  columns,
  tableInstance,
  expandedIds,
  setExpandedIds,
  handleRowClick,
  highlightOnClick,
  selectedRowId,
  columnStyling,
  rowStyling,
  columnColors,
  groupBy,
  columnWidths,
  style,
}) => {
  if (!rowInfo) return null;

  const rowData = rowInfo.rowData;
  const isSubRow = rowInfo.type === 'sub';

  const handleClick = () => {
    if (!isSubRow && rowData.__subRows && rowData.__subRows.length > 0) {
      setExpandedIds(prev =>
        prev.includes(rowInfo.groupId)
          ? prev.filter(id => id !== rowInfo.groupId)
          : [...prev, rowInfo.groupId]
      );
    }
    handleRowClick({ id: rowData.id, original: rowData });
  };

  const rowStyleOverride = rowStyling ? rowStyling(rowData) : {};

  return (
    <div
      style={{ ...style, ...rowStyleOverride }}
      className={`vt-table-row-wrapper ${highlightOnClick && rowData.id === selectedRowId ? 'vt-highlighted-row' : ''}`}
      onClick={handleClick}
      data-subrow={isSubRow ? "true" : "false"}
    >
      <div className="vt-table-row">
        {columns.map((col, colIndex) => {
          const columnInstance = tableInstance.allColumns.find(
            c => c.id === col.accessor || c.accessor === col.accessor
          );

          const columnStyleObj = columnStyling[col.accessor]
            ? columnStyling[col.accessor](rowData)
            : {};

          const { className: dynamicClassName, ...columnStyle } = columnStyleObj;

          const cellValue = columnInstance?.Cell
            ? columnInstance.Cell({
                value: rowData[col.accessor],
                row: { original: rowData }
              })
            : rowData[col.accessor];

          const cellStyle = {
            backgroundColor: columnColors[colIndex] ||
                            (colIndex === 0 ? 'var(--table-heading-color)' : 'transparent'),
            minWidth: columnWidths[colIndex],
            width: columnWidths[colIndex],
            paddingLeft: isSubRow && colIndex === 0 ? '2rem' : '0.5rem',
            ...columnStyle,
          };

          return (
              <div
                key={`${rowData.id}-${col.accessor}`}
                className={`vt-table-cell ${colIndex === 0 ? 'vt-sticky-column' : ''} ${dynamicClassName || ''}`}
                style={cellStyle}
              >
              {colIndex === 0 && rowData.__subRows && !isSubRow && (
                <span
                  className="vt-expand-toggle"
                  style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                >
                  {expandedIds.includes(rowInfo.groupId) ? '▼' : '►'}
                </span>
              )}
              <span className="vt-cell-content">
                {cellValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

const VirtualizedTablesTemplate = ({
  columns,
  eventHandlers,
  tableName,
  onRowClick,
  onRowDoubleClick,
  onEmptyTableClick,
  filterByInstrument,
  highlightOnClick,
  groupBy,
  subGroupBy,
  columnColors = {},
  selectedRowId: externalSelectedRowId,
  setSelectedRowId: externalSetSelectedRowId,
  enableSearch = false,
  defaultSortBy = [],
  columnStyling = {},
  rowStyling,
  enableNullPriceSortFix = false,
  side = undefined,
  enableBatching = true,
}) => {
  const dispatch = useDispatch();

  const userId = useSelector(state => state.globals?.tsUserId);
  const savedTableState = useSelector(state =>
    state.panelPositions?.tableStates?.[userId]?.[tableName] || {}
  );

  const [localSelectedRowId, setLocalSelectedRowId] = useState(null);
  const [localTableData, setLocalTableData] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);

  const [searchColumn, setSearchColumn] = useState(savedTableState.searchColumn || '');
  const [searchValue, setSearchValue] = useState(savedTableState.searchValue || '');

  const clickTimeout = useRef(null);
  const selectedRowId = externalSelectedRowId ?? localSelectedRowId;
  const setSelectedRowId = externalSetSelectedRowId ?? setLocalSelectedRowId;

  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const eventHandlersRef = useRef(eventHandlers);
  const listRef = useRef(null);
  const outerRef = useRef(null);
  const rowHeightCache = useRef({});

  const [containerHeight, setContainerHeight] = useState(600);
  const [containerWidth, setContainerWidth] = useState(800);
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

  const [columnWidths, setColumnWidths] = useState(() => {
    if (savedTableState.columnWidths && Array.isArray(savedTableState.columnWidths)) {
      return savedTableState.columnWidths;
    }
    return columns.map(col => col.minWidth ?? 120);
  });

  useEffect(() => {
    if (savedTableState.columnWidths && Array.isArray(savedTableState.columnWidths)) {
      setColumnWidths(savedTableState.columnWidths);
    }

    if (savedTableState.searchColumn !== undefined) {
      setSearchColumn(savedTableState.searchColumn);
    }

    if (savedTableState.searchValue !== undefined) {
      setSearchValue(savedTableState.searchValue);
    }
  }, [savedTableState]);

  const saveColumnWidthsDebounced = useRef(null);

  useEffect(() => {
    if (saveColumnWidthsDebounced.current) {
      clearTimeout(saveColumnWidthsDebounced.current);
    }

    saveColumnWidthsDebounced.current = setTimeout(() => {
      if (userId && tableName) {
        dispatch(updateTableColumnWidths({
          userId,
          tableId: tableName,
          columnWidths
        }));
      }
    }, 500);

    return () => {
      if (saveColumnWidthsDebounced.current) {
        clearTimeout(saveColumnWidthsDebounced.current);
      }
    };
  }, [columnWidths, userId, tableName, dispatch]);

  const startResize = (e, colIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = columnWidths[colIndex];

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;

      setColumnWidths(prev => {
        const next = [...prev];
        next[colIndex] = Math.max(60, startWidth + delta);
        return next;
      });

      if (listRef.current) {
        listRef.current.resetAfterIndex(0);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const totalTableWidth = useMemo(() => {
    return columnWidths.reduce((sum, width) => sum + width, 0);
  }, [columnWidths]);

  const listWidth = containerWidth > 0 ? containerWidth : totalTableWidth;

  useEffect(() => {
      eventHandlersRef.current = eventHandlers;
  }, [eventHandlers]);

  useEffect(() => {
      if (containerRef.current) {
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
              setContainerHeight(entry.contentRect.height);
              setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
      }
  }, []);

  useEffect(() => {
      const handleEvent = (event) => {
        if (!event.detail || !event.detail.type) return;

        if (event.detail.type === CLEAR_TABLE_DATA) {
            setLocalTableData([]);
            setExpandedIds([]);
            setSelectedRowId(null);
            return;
        }

        const currentHandler = eventHandlersRef.current;
        if (currentHandler) {
            currentHandler(event.detail, setLocalTableData);
        }
      };

      window.addEventListener(tableName, handleEvent);
      return () => {
        window.removeEventListener(tableName, handleEvent);
      };
  }, [tableName, setSelectedRowId]);

  const handleRowClick = useCallback((row) => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
        if (onRowDoubleClick) onRowDoubleClick(row.original);
      } else {
        clickTimeout.current = setTimeout(() => {
            if (onRowClick) onRowClick(row.original);
            clickTimeout.current = null;
        }, 250);
      }

      if (highlightOnClick) {
        setSelectedRowId(row.id);
      }
  }, [highlightOnClick, onRowClick, onRowDoubleClick, setSelectedRowId]);

  const instrumentFilteredData = useMemo(() => {
      if (filterByInstrument === undefined) return localTableData;
      if (!filterByInstrument) return [];
      return localTableData.filter(row => row && row.instr === filterByInstrument);
  }, [localTableData, filterByInstrument]);

  const userFilteredData = useMemo(() => {
      if (!enableSearch || !searchColumn || !searchValue) return instrumentFilteredData;
      return instrumentFilteredData.filter(row => {
        if (!row) return false;
        const cellValue = row[searchColumn];
        return cellValue !== undefined && String(cellValue).toLowerCase().includes(searchValue.toLowerCase());
      });
  }, [instrumentFilteredData, enableSearch, searchColumn, searchValue]);

  const groupedData = useMemo(() => {
      if (!groupBy || !subGroupBy) return userFilteredData;

      const grouped = {};
      for (const row of userFilteredData) {
        if (!row) continue;
        const key = row[groupBy];
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      }

      const finalRows = [];
      Object.entries(grouped).forEach(([groupKey, rows]) => {
        const sorted = [...rows].sort((a, b) => b[subGroupBy] - a[subGroupBy]);
        const [mainRow, ...subRows] = sorted;
        finalRows.push({ ...mainRow, __subRows: subRows, __groupId: groupKey });
      });

      return finalRows;
  }, [userFilteredData, groupBy, subGroupBy]);

  const finalData = useMemo(() => {
      if (!enableNullPriceSortFix) return groupedData;
      const copy = Array.isArray(groupedData) ? [...groupedData] : [];
      copy.sort((a, b) => {
        const aNum = Number(String(a?.price).replace(/,/g, ''));
        const bNum = Number(String(b?.price).replace(/,/g, ''));
        if (side === 'Buy') return bNum - aNum;
        if (side === 'Sell') return aNum - bNum;
        return aNum - bNum;
      });
      return copy;
  }, [groupedData, enableNullPriceSortFix, side]);

  const patchedColumns = useMemo(() => {
      if (!columns) return columns;
      return columns;
  }, [columns]);

  const initialSortBy = savedTableState.sortBy || defaultSortBy;

  const tableInstance = useTable({
      columns: patchedColumns,
      data: finalData,
      manualSortBy: !!enableNullPriceSortFix,
      initialState: {
        sortBy: initialSortBy,
      },
      autoResetSortBy: false,
  }, useSortBy);

  useEffect(() => {
    if (userId && tableName && tableInstance.state.sortBy) {
      dispatch(updateTableSorting({
        userId,
        tableId: tableName,
        sortBy: tableInstance.state.sortBy
      }));
    }
  }, [tableInstance.state.sortBy, userId, tableName, dispatch]);

  const saveSearchDebounced = useRef(null);

  useEffect(() => {
    if (saveSearchDebounced.current) {
      clearTimeout(saveSearchDebounced.current);
    }

    saveSearchDebounced.current = setTimeout(() => {
      if (userId && tableName) {
        dispatch(updateTableSearch({
          userId,
          tableId: tableName,
          searchColumn,
          searchValue
        }));
      }
    }, 500);

    return () => {
      if (saveSearchDebounced.current) {
        clearTimeout(saveSearchDebounced.current);
      }
    };
  }, [searchColumn, searchValue, userId, tableName, dispatch]);

  const preparedRows = useMemo(() => {
    return tableInstance.rows.map(row => {
      tableInstance.prepareRow(row);
      return row;
    });
  }, [tableInstance.rows, tableInstance]);

  const flattenedRows = useMemo(() => {
      const rows = [];

      preparedRows.forEach(row => {
        const rowData = row.original;
        if (!rowData) return;

        const groupId = groupBy ? rowData[groupBy] : rowData.id;
        const isExpanded = expandedIds.includes(groupId);

        rows.push({
            type: 'main',
            rowData,
            row,
            isGrouped: !!rowData.__subRows,
            groupId,
            isExpanded
        });

        if (isExpanded && rowData.__subRows) {
            rowData.__subRows.forEach((sub, index) => {
              const subRowData = { ...sub, __isSubRow: true };
              rows.push({
                  type: 'sub',
                  rowData: subRowData,
                  parentGroupId: groupId,
                  index
              });
            });
        }
      });

      return rows;
  }, [preparedRows, expandedIds, groupBy]);

  useEffect(() => {
      const headerHeight = enableSearch ? 100 : 52;
      const availableHeight = containerHeight - headerHeight;
      const totalContentHeight = flattenedRows.length * 35;
      setHasVerticalScroll(totalContentHeight > availableHeight);
  }, [flattenedRows.length, containerHeight, enableSearch]);

  useEffect(() => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(0);
      }
  }, [flattenedRows.length]);

  const getRowHeight = useCallback((index) => {
      return rowHeightCache.current[index] || 35;
  }, []);

  const Row = useCallback(({ index, style }) => {
      const rowInfo = flattenedRows[index];
      if (!rowInfo) return null;

      return (
        <RowComponent
            key={rowInfo.rowData.id || `row-${index}`}
            style={style}
            rowInfo={rowInfo}
            columns={columns}
            tableInstance={tableInstance}
            expandedIds={expandedIds}
            setExpandedIds={setExpandedIds}
            handleRowClick={handleRowClick}
            highlightOnClick={highlightOnClick}
            selectedRowId={selectedRowId}
            columnStyling={columnStyling}
            rowStyling={rowStyling}
            columnColors={columnColors}
            groupBy={groupBy}
            columnWidths={columnWidths}
        />
      );
  }, [flattenedRows, columns, tableInstance, expandedIds, handleRowClick, highlightOnClick, selectedRowId, columnStyling, rowStyling, columnColors, groupBy, columnWidths]);

  useEffect(() => {
      const outerElement = outerRef.current;
      const headerElement = headerRef.current;

      if (!outerElement || !headerElement) return;

      const handleScroll = (e) => {
        headerElement.scrollLeft = e.target.scrollLeft;
      };

      outerElement.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        outerElement.removeEventListener('scroll', handleScroll);
      };
  }, [containerWidth, containerHeight, flattenedRows.length]);

  const outerElementType = useMemo(
      () => React.forwardRef((props, ref) => {
        return (
            <div
              ref={(node) => {
                  outerRef.current = node;
                  if (typeof ref === 'function') {
                    ref(node);
                  } else if (ref) {
                    ref.current = node;
                  }
              }}
              {...props}
            />
        );
      }),
      []
  );

  const headerHeight = enableSearch ? 100 : 52;
  const SCROLLBAR_WIDTH = 15;

  return (
      <div className="vt-virtualized-table-container" ref={containerRef}>
        {enableSearch && (
            <div className="vt-search-controls">
              <select
                  value={searchColumn}
                  onChange={e => setSearchColumn(e.target.value)}
                  style={{ minWidth: '150px' }}
              >
                  <option value="">Search Column</option>
                  {columns.filter(col => col.accessor && col.Header).map(col => (
                    <option key={col.accessor} value={col.accessor}>
                        {col.Header}
                    </option>
                  ))}
              </select>
              <input
                  type="text"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  placeholder="Enter search value..."
                  style={{ flex: 1 }}
              />
            </div>
        )}

        <div
          ref={headerRef}
          className="vt-table-header-wrapper"
          style={{
            paddingRight: hasVerticalScroll ? `${SCROLLBAR_WIDTH}px` : '0px'
          }}
        >
          <div className="vt-table-header" style={{ minWidth: totalTableWidth }}>
            {tableInstance.headerGroups.map((headerGroup, idx) => (
              <div key={headerGroup.id || `header-${idx}`} className="vt-header-row">
                {headerGroup.headers.map((column, colIdx) => {
                  const headerProps = column.getHeaderProps(column.getSortByToggleProps());
                  const { key: _, ...safeHeaderProps } = headerProps;

                  return (
                    <div
                      key={column.id || `col-${colIdx}`}
                      className={`vt-table-header-cell ${colIdx === 0 ? 'vt-sticky-column' : ''}`}
                      style={{
                        minWidth: columnWidths[colIdx],
                        width: columnWidths[colIdx],
                        position: 'relative',
                      }}
                    >
                      <div
                        {...safeHeaderProps}
                        className="vt-header-click-area"
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingLeft: '8px',
                          paddingRight: '8px',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <span>{column.render('Header')}</span>
                        {column.isSorted && (
                          <span className="vt-sort-indicator">
                            {column.isSortedDesc ? '▼' : '▲'}
                          </span>
                        )}
                      </div>

                      <div
                        className="vt-resize-handle"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          startResize(e, colIdx);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="vt-table-body">
          {flattenedRows.length > 0 ? (
            <VariableSizeList
              key={`${containerWidth}-${flattenedRows.length}`}
              ref={listRef}
              height={containerHeight - headerHeight}
              itemCount={flattenedRows.length}
              itemSize={getRowHeight}
              width={listWidth}
              outerElementType={outerElementType}
              overscanCount={5}
            >
              {Row}
            </VariableSizeList>
          ) : (
            <div
              className="vt-no-data-message"
              onDoubleClick={() => {
                if (onEmptyTableClick) onEmptyTableClick();
              }}
              onClick={() => {
                if (onEmptyTableClick) onEmptyTableClick();
              }}
              style={{ cursor: 'pointer' }}
            >
              {localTableData.length === 0
                ? 'No data'
                : 'No matching rows found'}
            </div>
          )}
        </div>
      </div>
  );
};

export default VirtualizedTablesTemplate;